import React, { useMemo } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

export interface SeriesMetric<T> {
  key: string
  label: string
  color: string
  value: (row: T) => number
}

interface SeriesChartProps<T extends { day: string }> {
  rows: T[]
  metrics: SeriesMetric<T>[]
  title?: string
  height?: number
}

/** Daily lines for any series rows with a `day` (YYYY-MM-DD). */
export default function SeriesChart<T extends { day: string }>({
  rows,
  metrics,
  title,
  height = 320,
}: SeriesChartProps<T>) {
  const theme = useTheme()
  const data = useMemo(
    () =>
      rows.map((r) => ({
        date: new Date(`${r.day}T12:00:00`).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }),
        ...Object.fromEntries(metrics.map((m) => [m.key, m.value(r)])),
      })),
    [rows, metrics]
  )
  const empty = rows.every((r) => metrics.every((m) => m.value(r) === 0))

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
      )}
      {empty ? (
        <Box
          sx={{
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography color="text.secondary">Ingen data i perioden</Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              tickLine={false}
              axisLine={{ stroke: theme.palette.divider }}
              minTickGap={16}
            />
            <YAxis
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              tickLine={false}
              axisLine={false}
              width={44}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
              }}
              formatter={(value: number) => value.toLocaleString('da-DK')}
            />
            {metrics.length > 1 && <Legend wrapperStyle={{ paddingTop: 12 }} />}
            {metrics.map((m) => (
              <Line
                key={m.key}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  )
}
